import React from 'react';
import { LayoutDashboardIcon, UsersIcon, UserIcon, CalendarIcon, BriefcaseIcon, ShieldIcon, StethoscopeIcon, ContactIcon, TrendingUpIcon, SettingsIcon, LogOutIcon, FileTextIcon, ClipboardListIcon, MessageSquareIcon, ChevronsLeftIcon, ChevronsRightIcon, XIcon, BrainIcon, PercentIcon, PackageIcon, BotIcon, UserCircleIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useData } from '../../contexts/DataContext';
import { ModulePermissions } from '../../types';
interface SidebarProps {
  currentModule: string;
  onModuleChange: (module: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onMobileClose?: () => void;
}
const menuGroups = [{
  label: 'Principal',
  items: [{
    id: 'dashboard',
    label: 'Panel de Control',
    icon: LayoutDashboardIcon,
    permission: 'dashboard' as keyof ModulePermissions
  }, {
    id: 'patients',
    label: 'Pacientes',
    icon: UsersIcon,
    permission: 'patients' as keyof ModulePermissions
  }, {
    id: 'patient360',
    label: 'Cliente 360',
    icon: UserCircleIcon,
    permission: 'patient360' as keyof ModulePermissions
  }, {
    id: 'appointments',
    label: 'Citas',
    icon: CalendarIcon,
    permission: 'appointments' as keyof ModulePermissions
  }, {
    id: 'clinical-history',
    label: 'Historial Clínico',
    icon: FileTextIcon,
    permission: 'clinicalHistory' as keyof ModulePermissions
  }]
}, {
  label: 'Clínica',
  items: [{
    id: 'doctors',
    label: 'Doctores',
    icon: UserIcon,
    permission: 'doctors' as keyof ModulePermissions
  }, {
    id: 'services',
    label: 'Servicios',
    icon: BriefcaseIcon,
    permission: 'services' as keyof ModulePermissions
  }, {
    id: 'products',
    label: 'Productos',
    icon: PackageIcon,
    permission: 'products' as keyof ModulePermissions
  }, {
    id: 'insurance',
    label: 'Seguros',
    icon: ShieldIcon,
    permission: 'insurance' as keyof ModulePermissions
  }, {
    id: 'specialties',
    label: 'Especialidades',
    icon: StethoscopeIcon,
    permission: 'specialties' as keyof ModulePermissions
  }]
}, {
  label: 'CRM',
  items: [{
    id: 'contacts',
    label: 'Contactos',
    icon: ContactIcon,
    permission: 'contacts' as keyof ModulePermissions
  }, {
    id: 'opportunities',
    label: 'Oportunidades',
    icon: TrendingUpIcon,
    permission: 'opportunities' as keyof ModulePermissions
  }, {
    id: 'follow-up-notes',
    label: 'Notas de Seguimiento',
    icon: MessageSquareIcon,
    permission: 'followUpNotes' as keyof ModulePermissions
  }, {
    id: 'commissions',
    label: 'Comisiones',
    icon: PercentIcon,
    permission: 'commissions' as keyof ModulePermissions
  }, {
    id: 'intelligence',
    label: 'Inteligencia',
    icon: BrainIcon,
    permission: 'intelligence' as keyof ModulePermissions
  }, {
    id: 'bot-omnichannel',
    label: 'Bot Omnichannel',
    icon: BotIcon,
    permission: 'botOmnichannel' as keyof ModulePermissions
  }]
}, {
  label: 'Sistema',
  items: [{
    id: 'audit-log',
    label: 'Bitácora',
    icon: ClipboardListIcon,
    permission: 'auditLog' as keyof ModulePermissions
  }, {
    id: 'settings',
    label: 'Configuración',
    icon: SettingsIcon,
    permission: 'settings' as keyof ModulePermissions
  }]
}];
const LOGO_URL = "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/krfvfepnmlprsyech1wcsf/b2980652-5950-4085-85b3-5afd8262465c/images.jpg";
export function Sidebar({
  currentModule,
  onModuleChange,
  isCollapsed,
  onToggleCollapse,
  onMobileClose
}: SidebarProps) {
  const {
    logout,
    user
  } = useAuth();
  const {
    canView
  } = usePermissions();
  const {
    isLoading
  } = useData();
  return <aside className={`h-screen bg-white dark:bg-[#0D1117] border-r border-gray-100 dark:border-white/[0.05] flex flex-col overflow-y-auto overscroll-contain transition-all duration-300 ease-in-out shadow-[1px_0_0_0_rgba(0,0,0,0.06)] dark:shadow-none md:shadow-none ${isCollapsed ? 'w-[68px]' : 'w-72 md:w-64'}`} style={{
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(156, 163, 175, 0.2) transparent'
  }} data-id="element-182">
      
      {/* Logo + collapse + close */}
      <div className="flex-shrink-0 px-3 border-b border-gray-100 dark:border-white/[0.05] flex items-center justify-between min-h-[65px] gap-2" data-id="element-183">
        <button onClick={() => onModuleChange('dashboard')} className={`flex items-center gap-3 overflow-hidden hover:opacity-80 transition-opacity min-w-0 flex-1 ${isCollapsed ? 'justify-center' : ''}`} title="Ir al Panel de Control" data-id="element-184">
          
          {/* Logo con fallback de iniciales */}
          <div className="relative flex-shrink-0 w-9 h-9" data-id="element-185">
            {/* Fallback always behind */}
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#1BACA6] to-[#1890C8] flex items-center justify-center" data-id="element-186">
              <span className="text-white font-bold text-sm select-none" data-id="element-187">S</span>
            </div>
            {/* Image on top, clips itself */}
            <img src={LOGO_URL} alt="Savidna Logo" className="absolute inset-0 w-9 h-9 rounded-full object-cover" onError={e => {
            e.currentTarget.style.display = 'none';
          }} data-id="element-188" />
          </div>
          
          {!isCollapsed && <div className="min-w-0 text-left" data-id="element-189">
              <h1 className="text-sm font-bold text-gray-900 dark:text-white truncate leading-tight" data-id="element-190">
                Savidna
              </h1>
              <p className="text-[11px] text-gray-400 dark:text-gray-500 truncate" data-id="element-191">
                CRM Médico
              </p>
            </div>}
        </button>

        {/* Mobile close */}
        {onMobileClose && <button onClick={onMobileClose} className="md:hidden flex-shrink-0 p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-lg transition-colors" aria-label="Cerrar menú" data-id="element-192">
            <XIcon className="w-5 h-5" data-id="element-193" />
          </button>}
      </div>

      {/* Collapse toggle — desktop, below header */}
      <button onClick={onToggleCollapse} className="flex-shrink-0 hidden md:flex ml-3 my-2 items-center justify-center w-7 h-7 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-white/[0.05] dark:hover:bg-white/[0.09] text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-all duration-200" title={isCollapsed ? 'Expandir menú' : 'Colapsar menú'} data-id="element-194">
        {isCollapsed ? <ChevronsRightIcon className="w-3.5 h-3.5" data-id="element-195" /> : <ChevronsLeftIcon className="w-3.5 h-3.5" data-id="element-196" />}
      </button>

      {/* User info pill — mobile only */}
      {!isCollapsed && user && <div className="flex-shrink-0 md:hidden mx-3 mt-3 px-3 py-2.5 bg-gradient-to-r from-[#1BACA6]/8 to-[#1890C8]/5 dark:from-cyan-500/10 dark:to-teal-500/5 rounded-xl border border-[#1BACA6]/15 dark:border-cyan-500/10 flex items-center gap-2.5" data-id="element-197">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#1BACA6] to-[#1890C8] flex items-center justify-center flex-shrink-0 shadow-sm shadow-[#1BACA6]/25" data-id="element-198">
            {user.avatar ? <img src={user.avatar} alt={user.name} className="w-8 h-8 rounded-full object-cover" data-id="element-199" /> : <span className="text-white font-bold text-sm" data-id="element-200">
                {user.name?.charAt(0).toUpperCase()}
              </span>}
          </div>
          <div className="min-w-0" data-id="element-201">
            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate" data-id="element-202">
              {user.name}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 truncate" data-id="element-203">
              {user.email}
            </p>
          </div>
        </div>}

      

      {/* Navigation */}
      <nav className="flex-shrink-0 py-1" data-id="element-204">
        {menuGroups.map(group => {
        const visibleItems = group.items.filter(item => user && canView(item.permission));
        if (visibleItems.length === 0) return null;
        return <div key={group.label} className="mb-1" data-id="element-205">
              {!isCollapsed && <p className="px-4 pt-4 pb-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-gray-300 dark:text-gray-600 select-none" data-id="element-206">
                  {group.label}
                </p>}
              {isCollapsed && <div className="mx-3 my-2 h-px bg-gray-100 dark:bg-white/[0.05]" data-id="element-207" />}
              <ul className="space-y-0.5 px-2" data-id="element-208">
                {visibleItems.map(item => {
              const Icon = item.icon;
              const isActive = currentModule === item.id;
              return <li key={item.id} data-id="element-209">
                      <button onClick={() => onModuleChange(item.id)} title={isCollapsed ? item.label : undefined} className={`
                          w-full flex items-center gap-3 rounded-xl
                          text-[13px] font-medium transition-all duration-200 relative group
                          ${isCollapsed ? 'px-0 py-2.5 justify-center' : 'px-3 py-2.5'}
                          ${isActive ? 'bg-gradient-to-r from-[#1BACA6]/10 to-[#1890C8]/8 dark:from-cyan-500/15 dark:to-teal-500/8 text-[#1BACA6] dark:text-cyan-400' : 'text-gray-500 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-white/[0.04] hover:text-gray-800 dark:hover:text-gray-200'}
                        `} data-id="element-210">
                        
                        {isActive && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-gradient-to-b from-[#1BACA6] to-[#1890C8] rounded-r-full" data-id="element-211" />}
                        <Icon className={`w-[18px] h-[18px] flex-shrink-0 transition-colors duration-200 ${isActive ? 'text-[#1BACA6] dark:text-cyan-400' : ''}`} data-id="element-212" />
                        
                        {!isCollapsed && <span className="truncate" data-id="element-213">{item.label}</span>}
                      </button>
                    </li>;
            })}
              </ul>
            </div>;
      })}
      </nav>

      <div className="flex-grow" data-id="element-214" />

      {/* Logout */}
      <div className="flex-shrink-0 p-3 border-t border-gray-100 dark:border-white/[0.05]" style={{
      marginBottom: 'env(safe-area-inset-bottom)'
    }} data-id="element-215">
        
        <button onClick={() => {
        // Remove all session keys directly to guarantee clean logout
        try {
          localStorage.removeItem('savidna-session');
          localStorage.removeItem('savidna-user-id');
          const sbKey = Object.keys(localStorage).find(k => k.startsWith('sb-') && k.endsWith('-auth-token'));
          if (sbKey) localStorage.removeItem(sbKey);
          sessionStorage.clear();
        } catch {}
        window.location.href = window.location.origin;
      }} title={isCollapsed ? 'Cerrar Sesión' : undefined} className={`w-full flex items-center gap-3 rounded-xl text-[13px] font-medium text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-all duration-200 ${isCollapsed ? 'px-0 py-2.5 justify-center' : 'px-3 py-2.5'}`} data-id="element-216">
          
          <LogOutIcon className="w-[18px] h-[18px] flex-shrink-0" data-id="element-217" />
          {!isCollapsed && <span data-id="element-218">Cerrar Sesión</span>}
        </button>
      </div>
      {/* Mobile spacer to clear BottomNav */}
      <div className="flex-shrink-0 md:hidden h-14" aria-hidden="true" data-id="element-219" />
    </aside>;
}